import React, { useMemo, useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { Plus, LayoutList, Columns, Calendar as CalendarIcon, Search, X, ImageDown, ChevronUp, ChevronDown, ChevronsUpDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { MARK, SCHEDULE_CHAIN, DERIVED_EVENTS, SERVICE_TYPES, calendarDateOf, addDays, etapaPendente, etapasSemLancamento, esperandoLancamento } from '../../../constants';
import { prazosDoServico } from '../../../utils/calculations/prazos';
import AcoesDoServico from '../shared/AcoesDoServico';
import ServiceLabel from '../shared/ServiceLabel';
import MotivosDoServico, { motivosDoServico } from '../shared/MotivosDoServico';
import EstadoDoDinheiro, { FUNDO_QUITADO, estaQuitado } from '../shared/EstadoDoDinheiro';
import { gerarFichaDeCampo, etapaDaFicha, temFichaDeCampo } from '../../../utils/fichaDeCampo';
import { relogioDaTE } from '../../../utils/calculations/entrada';
import { pendenciasDoLaboratorio, filtraPorPendencia } from '../../../utils/calculations/pendencias';
import { situacaoNoContexto, linhasDeCobranca } from '../../../utils/calculations/proximoPasso';
import { identidadeDoCliente, localDoServico } from '../../../data/clients';
import { filtrarServicos } from '../../../data/services';
import { equipeDaEtapa, nomesDaEquipe, pessoasDasEquipes, estaNaEquipe, pessoa } from '../../../utils/equipe';
import { detalheDoEvento, periodoPorExtenso } from '../../../utils/calendarioExportado';
import ExportarCalendario from '../shared/ExportarCalendario';
import {
  isPregnancyCheckOverdue,
  getDaysSinceBilled,
} from '../../../utils';

/** 'YYYY-MM-DD' como o brasileiro lê. */
const dataCurta = (iso) => {
  const [a, m, d] = String(iso || '').slice(0, 10).split('-');
  return a ? `${d}/${m}/${a}` : '';
};

// ============================================================================
// HELPERS
// ============================================================================

/**
 * O cliente em cima; fazenda e contato embaixo.
 *
 * A regra mora em data/clients.js com teste, e o porquê da ordem está lá:
 * o contato já esteve em cima, e o dashboard virava uma coluna do mesmo nome,
 * porque um contato atende vários clientes.
 *
 * Helper de modulo porque PipelineView e KanbanView sao componentes irmaos,
 * fora do DashboardTab.
 */
const identidade = (service, clients = []) => {
  const cliente = clients.find(c => c.id === service.client_id);
  // Sem o cadastro em maos, o nome denormalizado no servico ainda serve.
  return identidadeDoCliente(cliente || { name: service.client_name });
};

const getCompositeStatus = (service) => {
  if (service.billed && !service.paid && getDaysSinceBilled(service) > 15) return 'red';
  // Etapa que ja devia ter sido lancada ha uma semana. Fica em amarelo e nao
  // em vermelho: e' trabalho que provavelmente FOI feito e nao foi registrado,
  // diferente de dinheiro que nao entrou.
  if (esperandoLancamento(service)) return 'yellow';
  if (isPregnancyCheckOverdue(service)) return 'yellow';
  return 'green';
};

const COMPOSITE_RANK = { red: 0, yellow: 1, green: 2 };

const StatusIndicator = ({ service }) => {
  const s = getCompositeStatus(service);
  if (s === 'red') return <span title="Fatura pendente há mais de 15 dias">{MARK.CRITICAL}</span>;
  if (s === 'yellow') {
    // O amarelo tem DOIS motivos e o aviso dizia sempre o segundo: quem passava
    // o mouse numa TE parada há 24 dias lia "DG60 atrasada" e ia procurar uma
    // DG60 que não existia.
    const paradas = etapasSemLancamento(service);
    const tip = paradas.length > 0
      ? paradas.map(e => `${e.label} agendada em ${dataCurta(e.agendada)}, sem lançamento há ${e.diasSemLancamento}d`).join('\n')
      : 'DG60 atrasada';
    return <span title={tip}>{MARK.WARN}</span>;
  }
  return null;
};

// Os tipos contratados sao rotulos, nao estados: a sigla ja diz qual e a etapa.
// Colorir cada uma exigiria mais cores do que a paleta tem, e era dai que vinha
// a colisao (FIV e EMB caiam ambos em slate). Chips neutros, sem colisao possivel.
const SERVICE_BADGES = SERVICE_TYPES
  .filter(t => t.id !== 'daily_service')
  .map(t => ({ id: t.id, label: t.sigla }));

const TypeBadges = ({ service }) => {
  const o = service.services_offered || [];
  return (
    <div className="flex gap-1 flex-wrap">
      {SERVICE_BADGES.filter(b => o.includes(b.id)).map(b => (
        <span key={b.id} className="px-1 py-0.5 text-xs bg-slate-100 text-slate-600 rounded font-medium">
          {b.label}
        </span>
      ))}
    </div>
  );
};

const DateCell = ({ scheduledDate, actualDate, done, label }) => {
  const display = actualDate || scheduledDate;
  if (!display) return <span className="text-slate-300 text-xs">-</span>;
  const tip = [
    label,
    scheduledDate && `Previsto: ${dataCurta(scheduledDate)}`,
    actualDate && `Realizado: ${dataCurta(actualDate)}`,
  ].filter(Boolean).join('\n');
  return (
    <span title={tip} className={`text-xs cursor-default ${done ? 'text-slate-900 font-semibold' : 'text-slate-500'}`}>
      {/* Sem o ano na célula: são seis colunas de data numa linha, e o ano é o
          mesmo em todas elas. Ele continua no `title`, escrito por extenso. */}
      {dataCurta(display).slice(0, 5)}{done && ` ${MARK.OK}`}
    </span>
  );
};

/**
 * Quanto de tela sobra do elemento para baixo, sem rolar.
 *
 * Mede até o fundo do conteúdo rolável mais próximo (o <main> do sistema), e
 * como se ele estivesse no topo: a conta não pode mudar porque alguém rolou.
 * `reserva` é o que fica embaixo e não é do elemento — o respiro do cartão e
 * da página.
 *
 * Mede a cada render, e não só no resize: a barra de filtros quebra em duas
 * linhas quando a busca ganha um "Limpar", e isso empurra a lista.
 */
const useAlturaDisponivel = (ref, reserva = 0) => {
  const [altura, setAltura] = useState(null);

  const medir = useCallback(() => {
    const el = ref.current;
    // Painel escondido (outra aba do sistema) mede zero, e zero não é resposta.
    if (!el || el.getClientRects().length === 0) return;
    let caixa = el.parentElement;
    while (caixa && !/(auto|scroll)/.test(getComputedStyle(caixa).overflowY)) {
      caixa = caixa.parentElement;
    }
    const topo = el.getBoundingClientRect().top + (caixa ? caixa.scrollTop : window.scrollY);
    const fundo = caixa
      ? caixa.getBoundingClientRect().top + caixa.clientHeight
      : window.innerHeight;
    const nova = Math.floor(fundo - topo - reserva);
    setAltura(a => (a === nova ? a : nova));
  }, [ref, reserva]);

  useLayoutEffect(medir);

  useEffect(() => {
    window.addEventListener('resize', medir);
    return () => window.removeEventListener('resize', medir);
  }, [medir]);

  return altura;
};

// O respiro abaixo do quadro: py-6 da página, p-4 do cartão e a borda dele.
const RESPIRO_DO_QUADRO = 24 + 16 + 1 + 3;

const ALTURA_DO_PAGINADOR = 40;
// Até a linha ser desenhada e medida: nome, contato e o número do serviço.
const LINHA_ESTIMADA = 56;
// Num celular deitado cabe quase nada; abaixo disto, rolar é melhor que paginar.
const MINIMO_POR_PAGINA = 3;

// ============================================================================
// PIPELINE VIEW  (sortable columns)
// ============================================================================

const PIPELINE_COLS = [
  { key: 'client',  label: 'Cliente',  sortFn: s => (s.client_name || '').toLowerCase() },
  { key: 'date',    label: 'Data',     sortFn: s => s.date || '' },
  ...SCHEDULE_CHAIN.map(stage => ({
    key: stage.id,
    label: stage.label,
    sortFn: s => ((s.services_offered || []).includes(stage.requires) && calendarDateOf(s, stage.id)) || '',
  })),
  { key: 'status',  label: 'Status',   sortFn: s => COMPOSITE_RANK[getCompositeStatus(s)] },
];

const SortIcon = ({ col, sortField, sortDir }) => {
  if (sortField !== col) return <ChevronsUpDown size={12} className="text-slate-400 ml-1 inline" />;
  return sortDir === 'asc'
    ? <ChevronUp   size={12} className="text-blue-600 ml-1 inline" />
    : <ChevronDown size={12} className="text-blue-600 ml-1 inline" />;
};

const PipelineView = ({ services, clients = [], getServiceStatus, openWorkflowModal,
                      imprimirFicha,
                       abrirCadastro, abrirFinanceiro, excluirServico = null,
                       podeFinanceiro = true,
                       sortField, sortDir, onSort, criticidade = () => ({ motivos: [] }),
                       servicoEmDestaque = null }) => {
  const sorted = useMemo(() => {
    const col = PIPELINE_COLS.find(c => c.key === sortField);
    if (!col) return services;
    return [...services].sort((a, b) => {
      const va = col.sortFn(a), vb = col.sortFn(b);
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ?  1 : -1;
      return 0;
    });
  }, [services, sortField, sortDir]);

  /**
   * PÁGINAS DO TAMANHO DA TELA.
   *
   * Era "Mostrar mais": a janela crescia de 25 em 25 e a página rolava junto.
   * Agora a lista cabe na tela e troca de página, sem rolagem vertical. O
   * tamanho da página não é um número cravado, é o quanto cabe: as linhas não
   * têm a mesma altura (aviso de etapa, motivo de cobrança, lote), então cada
   * uma é MEDIDA depois de desenhada, e as contas seguintes usam a medida.
   *
   * A página é ancorada na PRIMEIRA linha (`inicio`), e não num número de
   * página: quando uma medida corrige a estimativa, o fim da página se ajusta
   * e o começo fica onde estava — quem está olhando não vê a lista pular.
   *
   * A ordenação continua valendo para a lista inteira: a primeira página é
   * sempre o topo do que a ordenação promoveu.
   */
  const caixaRef = useRef(null);
  const cabecaRef = useRef(null);
  const corpoRef = useRef(null);
  const alturas = useRef(new Map());
  const [, setMedidas] = useState(0);
  const disponivel = useAlturaDisponivel(caixaRef, RESPIRO_DO_QUADRO);

  const orcamento = disponivel == null
    ? null
    : disponivel - (cabecaRef.current?.offsetHeight ?? 37) - ALTURA_DO_PAGINADOR;
  const alturaDe = (s) => alturas.current.get(s.id) ?? LINHA_ESTIMADA;

  // Quantas linhas cabem a partir de `de`, andando para frente.
  const cabemDesde = (de) => {
    if (orcamento == null) return MINIMO_POR_PAGINA;
    let usado = 0, n = 0;
    for (let i = de; i < sorted.length; i++) {
      usado += alturaDe(sorted[i]);
      if (usado > orcamento && n >= MINIMO_POR_PAGINA) break;
      n++;
    }
    return Math.max(n, 1);
  };

  // Onde começa a página anterior: anda para trás enchendo o mesmo orçamento.
  const inicioAntes = (ate) => {
    let usado = 0, i = ate;
    while (i > 0) {
      usado += alturaDe(sorted[i - 1]);
      if (orcamento != null && usado > orcamento && ate - i >= MINIMO_POR_PAGINA) break;
      i--;
    }
    return i;
  };

  const [inicio, setInicio] = useState(0);

  // Trocar o recorte ou a ordenação volta ao começo: a página 4 de outra
  // lista não é a página 4 desta.
  useEffect(() => { setInicio(0); }, [services.length, sortField, sortDir]);

  /**
   * O servico que acabou de nascer tem que aparecer — mesmo fora da página.
   *
   * Criar um servico que a ordenacao joga para a linha quarenta significaria
   * salvar e nao ver nada acontecer. Entao a lista vai ate a pagina dele.
   */
  const ondeEstaODestaque = servicoEmDestaque
    ? sorted.findIndex(s => s.id === servicoEmDestaque)
    : -1;

  useEffect(() => {
    if (ondeEstaODestaque < 0) return;
    // A página que começa nele é a única que com certeza o mostra.
    setInicio(i => (ondeEstaODestaque >= i && ondeEstaODestaque < i + cabemDesde(i)
      ? i : ondeEstaODestaque));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ondeEstaODestaque]);

  const comeco = Math.min(inicio, Math.max(0, sorted.length - 1));
  const visiveis = sorted.slice(comeco, comeco + cabemDesde(comeco));
  const fim = comeco + visiveis.length;

  // Mede as linhas desenhadas. Só pede outro render quando alguma medida mudou
  // de verdade, e cada linha estabiliza na primeira medida — o laço termina.
  // Sem dependências de propósito: toda página nova tem linhas por medir.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    const corpo = corpoRef.current;
    if (!corpo || corpo.getClientRects().length === 0) return;
    let mudou = false;
    corpo.querySelectorAll('tr[data-servico]').forEach(tr => {
      const id = tr.getAttribute('data-servico');
      const h = tr.getBoundingClientRect().height;
      const antes = alturas.current.get(id);
      if (antes == null || Math.abs(antes - h) > 1) {
        alturas.current.set(id, h);
        mudou = true;
      }
    });
    if (mudou) setMedidas(v => v + 1);
  });

  return (
    <div ref={caixaRef}>
    <div className="overflow-x-auto rounded-lg border border-slate-200">
      <table className="w-full text-sm">
        <thead ref={cabecaRef}>
          <tr className="bg-slate-50 border-b border-slate-200">
            {PIPELINE_COLS.map(col => (
              <th
                key={col.key}
                onClick={() => onSort(col.key)}
                className="text-left px-3 py-2 font-medium text-slate-700 whitespace-nowrap cursor-pointer select-none hover:bg-slate-100"
              >
                {col.label}
                <SortIcon col={col.key} sortField={sortField} sortDir={sortDir} />
              </th>
            ))}
            <th className="px-3 py-2" />
          </tr>
        </thead>
        <tbody ref={corpoRef} className="divide-y divide-slate-100">
          {visiveis.map(service => {
            const destaque = service.id === servicoEmDestaque;
            return (
            <tr
              key={service.id}
              data-servico={service.id}
              /* O destaque vence o verde: ele é temporário e existe para levar o
                 olho até a linha que o usuário acabou de pedir. */
              className={`transition-colors ${
                destaque ? 'bg-blue-50'
                  : estaQuitado(service) ? FUNDO_QUITADO
                    : 'hover:bg-slate-50'}`}
            >
              <td className="px-3 py-2">
                <div className="min-w-0">
                  <p className="font-medium text-slate-900 truncate max-w-[160px]">
                    {identidade(service, clients).principal}
                  </p>
                  {identidade(service, clients).secundario && (
                    <p className="text-xs text-slate-500 truncate max-w-[160px]">
                      {identidade(service, clients).secundario}
                    </p>
                  )}
                  {/* O que passou da data e ninguem lancou. Diz QUAL etapa e ha
                      quantos dias: "atrasado" sozinho nao diz onde procurar.

                      E LEVA ATE LA: o aviso nomeia a etapa, entao ele e' o
                      lugar natural de clicar. Antes era so' texto, e quem lia
                      "TE sem lancamento ha 24d" tinha que abrir o servico e
                      achar a aba da TE na mao — que nem sempre e' a aba que o
                      workflow escolhe sozinho, porque ele abre na etapa depois
                      da ultima concluida, e nao na mais atrasada. */}
                  {etapasSemLancamento(service).slice(0, 2).map(e => (
                    <button
                      key={e.id}
                      type="button"
                      onClick={() => openWorkflowModal(service, e.id)}
                      className="block text-xs text-blue-700 hover:text-blue-900 hover:underline truncate max-w-[160px] text-left"
                      title={`${e.label} agendada para ${dataCurta(e.agendada)} e ainda sem lançamento — abrir na aba ${e.label}`}>
                      {MARK.WARN} {e.label} sem lançamento há {e.diasSemLancamento}d
                    </button>
                  ))}
                  {/* Dinheiro atrasado, com o MOTIVO escrito. "Crítico"
                      sozinho manda a pessoa procurar o que está errado. */}
                  <MotivosDoServico
                    motivos={criticidade(service).motivos}
                    truncar
                    dataCurta={dataCurta}
                  />
                </div>
                <ServiceLabel service={service} />
              </td>
              <td className="px-3 py-2 text-xs text-slate-500 whitespace-nowrap">
                {dataCurta(service.date)}
              </td>
              {/* Só a data de etapa CONTRATADA. A de etapa descontratada (ou
                  herdada da origem, como a FIV de quem só faz TE) aparecia como
                  pendente numa coluna que não é deste serviço. */}
              {SCHEDULE_CHAIN.map(stage => (
                <td key={stage.id} className="px-3 py-2">
                  {(service.services_offered || []).includes(stage.requires) ? (
                    <DateCell
                      scheduledDate={service[stage.field]}
                      actualDate={service[stage.actualField]}
                      done={service[stage.doneField]}
                      label={stage.label}
                    />
                  ) : <DateCell />}
                </td>
              ))}
              {/* DUAS PERGUNTAS NA MESMA COLUNA, e elas não se substituem: a de
                  cima é a etapa ("aguardando TE"), a de baixo é o dinheiro
                  ("Pago"). Um serviço finalizado e quitado descia a lista sem
                  nenhum sinal, porque `StatusIndicator` cala justamente quando
                  está tudo em ordem. */}
              <td className="px-3 py-2">
                <div className="flex items-center gap-1">
                  <StatusIndicator service={service} />
                  <span className="text-xs text-slate-500 hidden xl:block">{getServiceStatus(service)}</span>
                </div>
                <EstadoDoDinheiro service={service} className="mt-1" />
              </td>
              <td className="px-3 py-2">
                {/* As três portas do serviço, no lugar do "Ver →".
                    Ele abria só as etapas, e cadastro e dinheiro exigiam
                    trocar de aba — era isso que fazia duas outras abas
                    existirem listando os mesmos serviços. */}
                <AcoesDoServico
                  service={service}
                  onCadastro={abrirCadastro}
                  onEtapas={(s) => openWorkflowModal(s)}
                  onFinanceiro={abrirFinanceiro}
                  /* Sem contexto de etapa aqui: vale a pendente, como no
                     resto da linha. `null` quando ela não tem ficha. */
                  onFicha={etapaDaFicha(service) ? imprimirFicha : null}
                  /* Só na LINHA da tabela. No cartão do Kanban e no balão do
                     calendário o alvo é pequeno e o clique do próprio cartão
                     está a um pixel — lixeira ali é acidente à espera. */
                  onExcluir={excluirServico}
                  podeFinanceiro={podeFinanceiro}
                />
              </td>
            </tr>
            );
          })}
          {sorted.length === 0 && (
            <tr>
              <td colSpan={9} className="px-3 py-10 text-center text-slate-400 text-sm">
                Nenhum serviço encontrado
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
    {sorted.length > 0 && (
      <div className="flex items-center justify-end gap-2 pt-2" style={{ height: ALTURA_DO_PAGINADOR }}>
        <span className="text-xs text-slate-500 tabular-nums">
          {comeco + 1}–{fim} de {sorted.length}
        </span>
        <button
          type="button"
          onClick={() => setInicio(inicioAntes(comeco))}
          disabled={comeco === 0}
          className="p-1 rounded border border-slate-300 bg-white text-slate-600 hover:bg-slate-50
                     disabled:opacity-40 disabled:cursor-default"
          aria-label="Página anterior">
          <ChevronLeft size={14} />
        </button>
        <button
          type="button"
          onClick={() => setInicio(fim)}
          disabled={fim >= sorted.length}
          className="p-1 rounded border border-slate-300 bg-white text-slate-600 hover:bg-slate-50
                     disabled:opacity-40 disabled:cursor-default"
          aria-label="Próxima página">
          <ChevronRight size={14} />
        </button>
      </div>
    )}
    </div>
  );
};

// ============================================================================
// KANBAN VIEW
// ============================================================================

// A coluna E a etapa pendente. Era uma escada de ifs com as cinco etapas
// escritas a mao -- etapa nova nao aparecia no kanban e o servico caia em
// "Finalizado" com ela em aberto.
const getKanbanColumn = (service) => etapaPendente(service)?.id || 'done';

// Sem cor por coluna: a posicao e o titulo ja identificam a etapa, e havia
// menos cores na paleta do que colunas (FIV, DG60 e Finalizado eram todas
// slate). "done" e a unica que muda de peso, para nao competir com as abertas.
// O titulo e vocabulario de tela, nao dado de agenda: a FIV nao e "FIV
// Pendente", e "Em Lab". Etapa sem verbete aqui cai em "<rotulo> Pendente".
const TITULO_DA_COLUNA = {
  protocolo: 'Protocolo Pendente',
  fiv: 'Em Lab (FIV)',
};

const KANBAN_COLUMNS = [
  ...SCHEDULE_CHAIN.map(e => ({
    id: e.id,
    label: TITULO_DA_COLUNA[e.id] || `${e.label} Pendente`,
  })),
  { id: 'done', label: 'Finalizado', muted: true },
];

const KanbanView = ({ services, clients = [], openWorkflowModal,
                     abrirCadastro, abrirFinanceiro, imprimirFicha,
                     podeFinanceiro = true }) => {
  const grouped = useMemo(() => {
    const result = {};
    KANBAN_COLUMNS.forEach(c => { result[c.id] = []; });
    services.forEach(s => result[getKanbanColumn(s)].push(s));
    return result;
  }, [services]);

  // A coluna rola por dentro, e a página não: ela vai até o fundo da tela e
  // para. Cabeçalho da coluna, bordas e a barra de rolagem horizontal saem da
  // conta.
  const caixaRef = useRef(null);
  const disponivel = useAlturaDisponivel(caixaRef, RESPIRO_DO_QUADRO);
  const alturaDaColuna = disponivel == null ? null : Math.max(200, disponivel - 60);

  return (
    <div ref={caixaRef}>
    <div className="overflow-x-auto pb-2">
      <div className="flex gap-3 min-w-max">
        {KANBAN_COLUMNS.map(col => {
          const items = grouped[col.id] || [];
          return (
            <div key={col.id} className={`w-52 flex flex-col border border-slate-200 rounded-lg ${col.muted ? 'bg-slate-50' : 'bg-white'}`}>
              <div className="px-3 py-2 rounded-t-lg border-b border-slate-200 flex items-center justify-between">
                <span className={`text-xs font-semibold ${col.muted ? 'text-slate-500' : 'text-slate-800'}`}>{col.label}</span>
                <span className="text-slate-600 bg-slate-100 text-xs font-semibold tabular-nums px-1.5 py-0.5 rounded-full">{items.length}</span>
              </div>
              {/* A altura vem da JANELA, e não de 480px cravados: num monitor
                  alto a coluna mostrava oito cartões e sobrava tela embaixo, e
                  num notebook de 768px ela passava da dobra. O dvh fica só
                  até a primeira medida. */}
              <div className="flex-1 overflow-y-auto max-h-[62dvh] p-2 space-y-2"
                style={alturaDaColuna ? { maxHeight: alturaDaColuna } : undefined}>
                {items.map(service => (
                  <div
                    key={service.id}
                    // A coluna E a etapa pendente, entao abrir dali abre nela.
                    // "Finalizado" nao e etapa: ali o workflow escolhe sozinho.
                    onClick={() => openWorkflowModal(service, col.id === 'done' ? null : col.id)}
                    className={`border border-slate-200 rounded-lg p-2 shadow-sm cursor-pointer
                                hover:shadow-md transition-shadow
                                ${estaQuitado(service) ? 'bg-emerald-50' : 'bg-white'}`}
                  >
                    <div className="flex items-start justify-between gap-1 mb-1">
                      <p className="text-xs font-semibold text-slate-900 leading-tight">
                        {identidade(service, clients).principal}
                      </p>
                      {identidade(service, clients).secundario && (
                        <p className="text-[11px] text-slate-500 leading-tight truncate">
                          {identidade(service, clients).secundario}
                        </p>
                      )}
                      <StatusIndicator service={service} />
                    </div>
                    <ServiceLabel service={service} />
                    <div className="mt-1">
                      <TypeBadges service={service} />
                    </div>
                    <p className="text-xs text-slate-400 mt-1">{dataCurta(service.date)}</p>
                    {service.te_quantity > 0 && <p className="text-xs text-slate-500 mt-0.5 font-medium">{service.te_quantity} TEs</p>}
                    <EstadoDoDinheiro service={service} className="mt-1" />

                    {/* Compacto: no cartão não há largura para três palavras,
                        e o ícone com `title` diz o mesmo. */}
                    <AcoesDoServico
                      service={service}
                      compacto
                      className="mt-1.5"
                      onCadastro={abrirCadastro}
                      onEtapas={(s) => openWorkflowModal(s, col.id === 'done' ? null : col.id)}
                      onFinanceiro={abrirFinanceiro}
                      /* A COLUNA É A ETAPA, então ela diz qual ficha sai —
                         mesma regra que o clique do cartão já usa para
                         escolher a aba. */
                      onFicha={etapaDaFicha(service, col.id)
                        ? (s) => imprimirFicha(s, col.id) : null}
                      podeFinanceiro={podeFinanceiro}
                    />
                  </div>
                ))}
                {items.length === 0 && <p className="text-xs text-slate-400 text-center py-6">Vazio</p>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
    </div>
  );
};

// ============================================================================
// CALENDAR VIEW
// ============================================================================

// Sao 7 tipos de evento para 4 familias de cor, entao a codificacao anterior
// tinha colisoes reais: Clivagem e TE saiam identicas, e FIV/Saida/DG60 tambem.
// Como o rotulo ja aparece escrito dentro do chip, a cor era redundante quando
// funcionava e enganosa quando colidia. Chip neutro, rotulo faz o trabalho.
//
// Rotulos e offsets vem de SCHEDULE_CHAIN/DERIVED_EVENTS: o calendario mostra
// a mesma agenda que o cadastro do servico monta, sem uma segunda copia dos
// numeros. O addDaysToStr local saiu junto — fazia `new Date('2026-08-17')`,
// que em fuso negativo volta um dia; o addDays do modulo trabalha em UTC.
/**
 * O que o calendário NÃO mostra.
 *
 * Ele responde "em que dia alguém tem de sair para algum lugar". Clivagem, FIV
 * e CIV acontecem na bancada, no dia seguinte ao anterior, sem deslocamento e
 * sem hora marcada — enchiam o mês de marcas que não mudam plano nenhum e
 * empurravam para fora do dia as que mudam: protocolo, retirada, OPU, TE e DG.
 *
 * Saem só da VISTA. Continuam em SCHEDULE_CHAIN e DERIVED_EVENTS, porque a
 * agenda inteira se apoia neles: a FIV é a âncora de onde o DG30 e o DG60
 * contam, e tirá-la da corrente derrubaria as duas datas que mais importam.
 */
const FORA_DO_CALENDARIO = new Set(['fiv', 'cleavage', 'civ']);

/**
 * Os prazos de cobrança entram no calendário como qualquer outro evento.
 *
 * Não é etapa — ninguém sai para lugar nenhum —, mas é a mesma pergunta que o
 * calendário responde: o que precisa de ação neste dia. Dinheiro que vence é
 * ação, e até aqui só aparecia em quem abrisse o Financeiro e procurasse.
 */
const EVENTOS_DE_PRAZO = {
  entrada: { label: 'Prazo entrada' },
  // Quem fatura na propria TE nao tem entrada, e tem os mesmos 21 dias.
  pagamento: { label: 'Prazo pagamento' },
  parcela: { label: 'Vencimento' },
};

/**
 * A cor de um evento no calendário.
 *
 * Aqui a cor sai da regra de só marcar affordance, e de propósito: num quadro
 * de sete colunas iguais, dinheiro que vence tinha exatamente a mesma cara de
 * uma OPU agendada, e a diferença é que um deles envelhece contra o
 * laboratório. O emoji continua no vencido; a cor é o que faz a pessoa achar o
 * evento sem ler as sete colunas.
 *
 * AS ETAPAS DE CAMPO GANHARAM COR PRÓPRIA, a pedido da equipe: no Google
 * Agenda eles pintavam à mão protocolo de verde e OPU de azul, e é assim que
 * leem o mês. A cor é redundante — o rótulo continua dizendo a etapa —, então
 * tirar a cor (impressão P&B, daltonismo) não perde informação. Retirada é o
 * mesmo protocolo e fica no verde; DG fica neutro, porque é a etapa que
 * menos muda o plano da semana e um quinto tom já seria vitral.
 */
const COR_DA_ETAPA = {
  protocolo: 'bg-emerald-100 text-emerald-900 border-emerald-600 hover:bg-emerald-200',
  retirada:  'bg-emerald-100 text-emerald-900 border-emerald-600 hover:bg-emerald-200',
  opu:       'bg-sky-100 text-sky-900 border-sky-600 hover:bg-sky-200',
  te:        'bg-violet-100 text-violet-900 border-violet-600 hover:bg-violet-200',
};
const COR_NEUTRA = 'bg-slate-100 text-slate-700 border-slate-400 hover:bg-slate-200';

const corDoEvento = (ev, venceu) => {
  if (!ev.prazo) return COR_DA_ETAPA[ev.type] || COR_NEUTRA;
  if (venceu) return 'bg-rose-50 text-rose-900 border-rose-600 hover:bg-rose-100';
  return 'bg-amber-50 text-amber-900 border-amber-500 hover:bg-amber-100';
};

/**
 * A legenda é também o liga/desliga, como as agendas do Google Agenda: o
 * quadradinho colorido é a caixa de marcar. Segue a ordem em que as etapas
 * acontecem. `tipos` são os eventos que cada item acende ou apaga — os prazos
 * de cobrança andam juntos, porque ninguém quer ver a entrada e esconder a
 * parcela.
 */
const LEGENDA = [
  { chave: 'protocolo', rotulo: 'Protocolo', cor: 'bg-emerald-500', borda: 'border-emerald-500', tipos: ['protocolo'] },
  { chave: 'retirada',  rotulo: 'Retirada',  cor: 'bg-emerald-500', borda: 'border-emerald-500', tipos: ['retirada'] },
  { chave: 'opu',       rotulo: 'OPU',       cor: 'bg-sky-500',     borda: 'border-sky-500',     tipos: ['opu'] },
  { chave: 'te',        rotulo: 'TE',        cor: 'bg-violet-500',  borda: 'border-violet-500',  tipos: ['te'] },
  { chave: 'dg30',      rotulo: 'DG30',      cor: 'bg-slate-400',   borda: 'border-slate-400',   tipos: ['dg30'] },
  { chave: 'dg60',      rotulo: 'DG60',      cor: 'bg-slate-400',   borda: 'border-slate-400',   tipos: ['dg60'] },
  { chave: 'prazo',     rotulo: 'Prazos',    cor: 'bg-amber-500',   borda: 'border-amber-500',   tipos: Object.keys(EVENTOS_DE_PRAZO) },
];
const ITEM_DA_LEGENDA = Object.fromEntries(LEGENDA.flatMap(l => l.tipos.map(t => [t, l.chave])));

// O que cada pessoa apagou fica no navegador dela, como no Google: é gosto de
// quem olha, não dado do laboratório.
const CHAVE_DOS_OCULTOS = 'calendario.ocultos';
const lerOcultos = () => {
  try {
    const salvo = JSON.parse(localStorage.getItem(CHAVE_DOS_OCULTOS) || '[]');
    return new Set(Array.isArray(salvo) ? salvo : []);
  } catch { return new Set(); }
};
const gravarOcultos = (ocultos) => {
  try { localStorage.setItem(CHAVE_DOS_OCULTOS, JSON.stringify([...ocultos])); } catch { /* aba anônima */ }
};

/**
 * A opção "Sem responsável" do filtro de pessoa: o que ainda falta escalar.
 *
 * Só etapa por FAZER. A equipe do serviço é recente, e etapa lançada guarda o
 * técnico na sessão — sem esse corte, todo serviço antigo apareceria como
 * "sem responsável" e afogaria o que falta decidir. A retirada segue o
 * protocolo: é a mesma equipe, e some quando a implantação é lançada.
 */
const SEM_RESPONSAVEL = '__sem_responsavel__';
const etapaDoEvento = (tipo) => (tipo === 'retirada' ? 'protocolo' : tipo);
const faltaResponsavel = (service, tipo) => {
  const etapa = SCHEDULE_CHAIN.find(e => e.id === etapaDoEvento(tipo));
  if (!etapa || service[etapa.doneField]) return false;
  return !pessoa(equipeDaEtapa(service.equipe_das_etapas, tipo), 'responsavel');
};

const EVENT_TYPES = Object.fromEntries([
  ...SCHEDULE_CHAIN.map(stage => [stage.id, { label: stage.label }]),
  ...DERIVED_EVENTS.map(ev => [ev.id, { label: ev.label }]),
  ...Object.entries(EVENTOS_DE_PRAZO),
]);

/**
 * A aba do workflow que cada evento do calendário abre.
 *
 * DG30 e DG60 dividem a de Prenhez, e os eventos DERIVADOS caem na etapa de
 * que pendem: clivagem e CIV são fatos da FIV, retirada é do protocolo, saída
 * é da TE. Evento de PRAZO não tem etapa — quem clica num vencimento quer o
 * financeiro, e ali `null` deixa o modal abrir onde a corrente indicar.
 */
const ABA_DO_EVENTO = {
  protocolo: 'protocolo', retirada: 'protocolo',
  opu: 'opu',
  fiv: 'fiv', cleavage: 'fiv', civ: 'fiv',
  te: 'te',
  dg30: 'pregnancy', dg60: 'pregnancy',
};

/**
 * A ficha que cada evento imprime. Não é a aba: DG30 e DG60 dividem a aba de
 * Prenhez, mas cada um tem a sua ficha — a do DG60 nem tem as mesmas colunas.
 */
const fichaDoEvento = (type) => (temFichaDeCampo(type) ? type : ABA_DO_EVENTO[type]);

/**
 * O balão do evento do calendário.
 *
 * A célula do calendário é estreita — no celular, muito —, e o evento cabia
 * cortado: "FIV Fazenda Sant..." não identifica serviço nenhum. O `title` do
 * HTML resolvia no desktop e NÃO EXISTE no toque, que é justamente onde o
 * corte é pior.
 *
 * Por isso o toque abre isto, e não o serviço direto: primeiro se lê o que é,
 * depois se decide entrar. São dois toques, e o segundo é opcional — antes o
 * único jeito de saber em que serviço se estava clicando era clicar.
 *
 * Posição calculada depois de medir, não estimada: o balão é ancorado no
 * evento, mas grudado na viewport, senão o evento da última coluna abriria
 * metade fora da tela do celular.
 */
const BalaoDoEvento = ({ evento, ancora, onFechar, onCadastro, onEtapas, onFinanceiro, onFicha }) => {
  const ref = useRef(null);
  const [pos, setPos] = useState(null);

  useLayoutEffect(() => {
    if (!ref.current || !ancora) return;
    const { width, height } = ref.current.getBoundingClientRect();
    const margem = 8;
    const left = Math.min(Math.max(margem, ancora.left), window.innerWidth - width - margem);
    // Abre para baixo; se não couber, vira para cima do evento.
    const abaixo = ancora.bottom + 6;
    const top = abaixo + height + margem <= window.innerHeight
      ? abaixo
      : Math.max(margem, ancora.top - height - 6);
    setPos({ left: Math.max(margem, left), top });
  }, [ancora]);

  /**
   * Fecha no Esc e ao girar o aparelho. NÃO fecha ao rolar, de propósito.
   *
   * Rolar desancora o balão do evento, o que é feio — mas o navegador rola o
   * botão focado para dentro da tela quando ele é tocado, então fechar no
   * scroll fecharia o balão no mesmo toque que o abriu, no celular. Balão
   * solto é melhor que balão que não abre.
   */
  useEffect(() => {
    const tecla = (e) => { if (e.key === 'Escape') onFechar(); };
    window.addEventListener('resize', onFechar);
    window.addEventListener('keydown', tecla);
    return () => {
      window.removeEventListener('resize', onFechar);
      window.removeEventListener('keydown', tecla);
    };
  }, [onFechar]);

  const s = evento.service;

  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onFechar} />
      <div
        ref={ref}
        role="dialog"
        style={{ left: pos?.left ?? 0, top: pos?.top ?? 0, visibility: pos ? 'visible' : 'hidden' }}
        className="fixed z-50 w-64 max-w-[calc(100vw-1rem)] bg-white border border-slate-300 rounded-lg shadow-xl p-3"
      >
        <div className="flex items-start justify-between gap-2 mb-2">
          <div>
            <p className="text-sm font-semibold text-slate-900">{EVENT_TYPES[evento.type].label}</p>
            <p className="text-xs text-slate-500">{dataCurta(evento.data)}</p>
          </div>
          <button onClick={onFechar} className="text-slate-400 hover:text-slate-700 -mt-0.5 -mr-0.5 p-1">
            <X size={16} />
          </button>
        </div>

        {/* Sem truncar: o balão existe para mostrar o que a célula corta. */}
        <p className="text-sm text-slate-800 break-words">{s.client_name || 'Sem cliente'}</p>
        {s.service_label && (
          <p className="text-xs text-slate-600 break-words mt-0.5">{s.service_label}</p>
        )}
        {s.service_code && (
          <p className="text-xs font-mono text-slate-400 mt-0.5">#{s.service_code}</p>
        )}

        {/* Prazo de pagamento diz QUANTO, e avisa quando já passou: a data no
            topo do balão sozinha não distingue "vence" de "venceu". */}
        {evento.prazo && (
          <p className="text-xs mt-2 text-slate-700">
            {evento.prazo.valor > 0 && (
              <span className="font-semibold">
                R$ {Number(evento.prazo.valor).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </span>
            )}
            {evento.data < new Date().toISOString().slice(0, 10)
              ? <span className="block text-slate-800 font-medium">{MARK.WARN} Vencido</span>
              : <span className="block text-slate-500">{evento.prazo.rotulo}</span>}
          </p>
        )}

        {/* AS TRÊS PORTAS, e não só "Abrir serviço".
            O botão levava sempre às etapas. Quem clica numa parcela vencida no
            calendário quer o financeiro, não a aba da OPU — e quem clica numa
            TE agendada quer lançar. O balão não tinha como saber qual, e agora
            não precisa: oferece os três. */}
        {/* Duas colunas desde que a ficha virou a quarta porta: em três, o
            quarto botão descia sozinho numa linha só para ele. O balão tem
            256px, e "Financeiro" é a palavra mais larga — cabe em metade. */}
        <div className="mt-3 grid grid-cols-2 gap-1.5">
          {[
            ['Cadastro', onCadastro],
            ['Etapas', onEtapas],
            ...(onFinanceiro ? [['Financeiro', onFinanceiro]] : []),
            ...(onFicha ? [['Ficha', onFicha]] : []),
          ].map(([rotulo, acao]) => (
            <button
              key={rotulo}
              onClick={acao}
              className="text-xs font-medium py-2 rounded-lg border border-slate-300 bg-white
                         text-slate-700 hover:bg-slate-50 transition-colors"
            >
              {rotulo}
            </button>
          ))}
        </div>
      </div>
    </>
  );
};

const CalendarView = ({ services, openWorkflowModal, abrirCadastro, abrirFinanceiro,
                       imprimirFicha,
                       podeFinanceiro = true,
                       // Vai no topo da imagem exportada: quem recebe no
                       // WhatsApp não sabe de que laboratório ela veio.
                       laboratorio = null,
                       // O resumo de gestão: busca o financeiro do laboratório,
                       // nomeia clientes e lista o que está em aberto hoje.
                       laboratoryId = null, clients = [], pendencias = null,
                       cobranca = [],
                       parcelasPorServico = {}, entradaPagaPorServico = {},
                       // So para o prazo de quem fatura na propria TE: o que
                       // vence ali e o valor do servico.
                       calculateServiceValue = () => 0,
                       // Clicar num dia abre um serviço com a OPU nele.
                       // `null` (papel sem permissão) deixa o dia inerte.
                       novoServico = null }) => {
  const today = new Date();

  /*
   * O FILTRO POR CLIENTE OU TRABALHO.
   *
   * O calendário não aceitava o recorte do quadro, e continua não aceitando:
   * "Finalizados" esconderia eventos e o dia pareceria livre sem estar. Este é
   * outro recorte, e responde outra pergunta — a do cliente que liga querendo
   * saber "quais são os dias dos meus trabalhos?". Uma linha avisa que o
   * calendário está filtrado, para ninguém ler um dia vazio como dia livre.
   */
  const [filtro, setFiltro] = useState('');
  const clientePorId = useMemo(
    () => Object.fromEntries(clients.map(c => [c.id, c])), [clients]);
  const clienteDo = useCallback(
    (s) => clientePorId[s.client_id] || { name: s.client_name }, [clientePorId]);
  const servicosVisiveis = useMemo(
    () => (filtro.trim()
      ? filtrarServicos(services, { busca: filtro }, s => identidadeDoCliente(clienteDo(s)))
      : services),
    [services, filtro, clienteDo]);

  /*
   * O FILTRO POR PESSOA.
   *
   * A busca já achava técnico pelo nome, mas recortava SERVIÇOS: buscar o
   * Breno trazia a TE que outro fez e os prazos de pagamento do serviço onde
   * ele só ajudou na OPU. Este recorta EVENTOS — só os dias em que ele é
   * responsável ou ajudante daquela etapa. Prazo de pagamento sai: não é dia
   * de ninguém ir à fazenda.
   */
  const [quem, setQuem] = useState('');
  const pessoas = useMemo(() => pessoasDasEquipes(services), [services]);
  const nomeDeQuem = pessoas.find(p => p.chave === quem)?.nome || '';

  const [ocultos, setOcultos] = useState(lerOcultos);
  const alternar = (chave) => setOcultos(prev => {
    const proximo = new Set(prev);
    if (proximo.has(chave)) proximo.delete(chave); else proximo.add(chave);
    gravarOcultos(proximo);
    return proximo;
  });
  const rotulosOcultos = LEGENDA.filter(l => ocultos.has(l.chave)).map(l => l.rotulo);

  // `ancora` é o retângulo do evento clicado, congelado: é dele que o balão
  // sai, e o elemento pode sair de tela antes de o balão fechar.
  const [balao, setBalao] = useState(null);
  const [exportando, setExportando] = useState(false);

  const events = useMemo(() => {
    const map = {};
    const add = (dateStr, type, service, prazo) => {
      if (!dateStr) return;
      if (ocultos.has(ITEM_DA_LEGENDA[type])) return;
      const key = String(dateStr).slice(0, 10);
      if (!map[key]) map[key] = [];
      map[key].push({ type, service, data: key, prazo });
    };

    servicosVisiveis.forEach(service => {
      const offered = service.services_offered || [];

      // Etapas com data propria. So entram as contratadas — antes a TE era
      // lancada mesmo em servico que nao tem TE, e so nao aparecia porque a
      // data vinha nula.
      // Com uma pessoa escolhida, só entra a etapa em que ela está escalada.
      // "Sem responsável" troca a pergunta: em vez de quem, ninguém.
      const daPessoa = (etapa) => {
        if (!quem) return true;
        if (quem === SEM_RESPONSAVEL) return faltaResponsavel(service, etapa);
        return estaNaEquipe(equipeDaEtapa(service.equipe_das_etapas, etapa), quem);
      };

      SCHEDULE_CHAIN.forEach(stage => {
        if (FORA_DO_CALENDARIO.has(stage.id)) return;
        if (!daPessoa(stage.id)) return;
        if (!offered.includes(stage.requires)) return;
        add(calendarDateOf(service, stage.id), stage.id, service);
      });

      // Derivados: Retirada a partir do protocolo, Saida junto da TE.
      DERIVED_EVENTS.forEach(ev => {
        if (FORA_DO_CALENDARIO.has(ev.id)) return;
        if (!offered.includes(ev.requires)) return;
        if (!daPessoa(ev.id)) return;
        add(addDays(calendarDateOf(service, ev.from), ev.offset), ev.id, service);
      });

      // Prazos de pagamento: a entrada (TE + 21) e cada parcela em aberto.
      if (quem) return;
      prazosDoServico(
        service,
        parcelasPorServico[service.id] || [],
        entradaPagaPorServico[service.id] || 0,
        { relogio: relogioDaTE(service), valor: calculateServiceValue(service) },
      ).forEach(p => add(p.data, p.tipo, service, p));
    });
    return map;
    // `calculateServiceValue` fica fora de proposito: ela e recriada a cada
    // render do FIVSystem, e listar aqui refaria o mapa inteiro sempre —
    // exatamente o que este useMemo existe para evitar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [servicosVisiveis, parcelasPorServico, entradaPagaPorServico, quem, ocultos]);

  /**
   * SEMANA e o padrao, e o mes continua a um clique.
   *
   * O mes cabe trinta e cinco celulas numa tela, e cada uma virava uma tira de
   * setenta pixels que so aguentava tres eventos truncados mais um "+2 mais".
   * A semana usa as mesmas sete colunas para sete dias: cada celula fica cinco
   * vezes maior, mostra TODOS os eventos do dia, e cabe dizer de quem e, de que
   * lote e, no caso de prazo, quanto.
   *
   * O mes fica porque responde outra pergunta — "o que vem pela frente" —, e e
   * ela que faz alguem remarcar uma OPU antes de o mes virar.
   */
  const [modo, setModo] = useState('semana');
  // Um dia DENTRO do periodo visivel. Semana e mes se derivam dele, entao
  // trocar de modo nao perde onde a pessoa estava.
  const [ancora, setAncora] = useState(() => today.toISOString().slice(0, 10));
  const todayStr = today.toISOString().split('T')[0];

  const diaDaSemana = (iso) => {
    const [y, m, d] = iso.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  };

  const dias = useMemo(() => {
    if (modo === 'semana') {
      const inicio = addDays(ancora, -diaDaSemana(ancora));
      return Array.from({ length: 7 }, (_, i) => addDays(inicio, i));
    }
    const [y, m] = ancora.split('-').map(Number);
    const primeiro = `${y}-${String(m).padStart(2, '0')}-01`;
    const total = new Date(Date.UTC(y, m, 0)).getUTCDate();
    // Os vazios do comeco mantem o dia 1 na coluna do seu dia da semana.
    return [
      ...Array(diaDaSemana(primeiro)).fill(null),
      ...Array.from({ length: total }, (_, i) => addDays(primeiro, i)),
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modo, ancora]);

  const andar = (passo) => setAncora((a) => {
    if (modo === 'semana') return addDays(a, 7 * passo);
    const [y, m] = a.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1 + passo, 1)).toISOString().slice(0, 10);
  });

  const nomeDoMes = (iso) => new Date(Date.UTC(+iso.slice(0, 4), +iso.slice(5, 7) - 1, 1))
    .toLocaleDateString('pt-BR', { month: 'long', timeZone: 'UTC' });

  // A semana por extenso mora em calendarioExportado: a imagem usa o mesmo.
  const titulo = modo === 'mes'
    ? `${nomeDoMes(ancora)} de ${ancora.slice(0, 4)}`
    : periodoPorExtenso(dias[0], dias[6]);

  // O período da tela, que é o que o exportar oferece primeiro.
  const periodoNaTela = modo === 'semana'
    ? [dias[0], dias[6]]
    : [dias.find(Boolean), dias[dias.length - 1]];

  /*
   * A célula cresce com o monitor, e não com um número escolhido a dedo.
   *
   * Na semana são sete colunas de um dia cada, e o que cabe nelas é o assunto
   * da visão: 52dvh mostra o dobro de eventos num monitor de 1440px de altura do
   * que as 16rem fixas mostravam, e continua caindo para 14rem no celular, onde
   * metade da tela já é o bastante para rolar dentro da célula.
   *
   * No mês são cinco ou seis linhas, então a fração é pequena: 12dvh por linha
   * dá um mês inteiro em tela cheia sem empurrar a lista de serviços para fora.
   */
  /*
   * O MÊS PAROU DE CORTAR. Eram três eventos truncados e um "+2 mais" numa
   * tira de 12dvh — "muito pequenininho", como a do Google. Agora a linha
   * cresce até caber o dia mais cheio da semana, e cada evento diz de quem é.
   * Rolar a página para ver o mês inteiro é melhor que abrir dia por dia.
   */
  const alturaDaCelula = modo === 'semana'
    ? 'min-h-56 sm:min-h-[52dvh] overflow-y-auto'
    : 'min-h-24 sm:min-h-[16dvh]';


  return (
    <div>
      {/*
        * A barra de navegacao, no jeito de agenda.
        *
        * Hoje, as setas e o titulo ficam JUNTOS, do mesmo lado: as setas so
        * fazem sentido ao lado daquilo que elas movem, e jogadas num canto
        * oposto ao periodo viravam dois controles sem relacao aparente.
        *
        * O periodo virou um seletor, e nao duas abas. Aba promete telas
        * paralelas; aqui e a MESMA tela em dois zooms, e a semana e a que se
        * usa. O mes mora dentro do seletor porque e a excecao consultada, nao
        * um destino concorrente.
        */}
      <div className="flex items-center gap-1.5 sm:gap-2 mb-4">
        <button
          onClick={() => setAncora(todayStr)}
          className="px-3 py-1.5 text-sm text-slate-700 bg-white border border-slate-300
                     rounded-full hover:bg-slate-50 transition-colors whitespace-nowrap"
        >
          Hoje
        </button>

        <button
          onClick={() => andar(-1)}
          aria-label={modo === 'semana' ? 'Semana anterior' : 'Mes anterior'}
          className="p-1.5 text-slate-500 rounded-full hover:bg-slate-100 hover:text-slate-800 transition-colors"
        >
          <ChevronLeft size={20} />
        </button>
        <button
          onClick={() => andar(1)}
          aria-label={modo === 'semana' ? 'Proxima semana' : 'Proximo mes'}
          className="p-1.5 text-slate-500 rounded-full hover:bg-slate-100 hover:text-slate-800 transition-colors"
        >
          <ChevronRight size={20} />
        </button>

        <h3 className="text-base sm:text-xl font-normal text-slate-800 capitalize truncate ml-1">
          {titulo}
        </h3>

        <select
          value={modo}
          onChange={(e) => setModo(e.target.value)}
          aria-label="Periodo do calendario"
          className="ml-auto text-sm text-slate-700 bg-white border border-slate-300 rounded-md
                     px-2.5 py-1.5 hover:bg-slate-50 cursor-pointer"
        >
          <option value="semana">Semana</option>
          <option value="mes">Mes</option>
        </select>

        <button
          onClick={() => setExportando(true)}
          title="Exportar agenda ou resumo em imagem"
          aria-label="Exportar calendário"
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-slate-700 bg-white
                     border border-slate-300 rounded-md hover:bg-slate-50 transition-colors"
        >
          <ImageDown size={16} />
          <span className="hidden sm:inline">Exportar</span>
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-3">
        <div className="relative w-full sm:w-72">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
            placeholder="Filtrar por cliente, fazenda, trabalho ou técnico"
            aria-label="Filtrar calendario por cliente, fazenda, trabalho ou tecnico"
            className="w-full pl-8 pr-8 py-1.5 text-sm border border-slate-300 rounded-md
                       focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {filtro && (
            <button
              onClick={() => setFiltro('')}
              aria-label="Limpar filtro"
              className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-700"
            >
              <X size={14} />
            </button>
          )}
        </div>
        <select
          value={quem}
          onChange={(e) => setQuem(e.target.value)}
          aria-label="Filtrar calendario por tecnico ou ajudante"
          className="text-sm text-slate-700 bg-white border border-slate-300 rounded-md
                     px-2.5 py-1.5 hover:bg-slate-50 cursor-pointer"
        >
          <option value="">Toda a equipe</option>
          <option value={SEM_RESPONSAVEL}>Sem responsável</option>
          {pessoas.map(p => <option key={p.chave} value={p.chave}>{p.nome}</option>)}
        </select>
        {quem === SEM_RESPONSAVEL ? (
          <p className="text-xs text-slate-600">
            {MARK.WARN} Só as etapas por fazer que ainda não têm responsável.
          </p>
        ) : quem ? (
          <p className="text-xs text-slate-600">
            {MARK.WARN} Só as etapas de {nomeDeQuem} — etapa sem equipe escolhida não aparece.
          </p>
        ) : filtro.trim() ? (
          <p className="text-xs text-slate-600">
            {MARK.WARN} Só {servicosVisiveis.length === 1 ? '1 trabalho' : `${servicosVisiveis.length} trabalhos`} —
            dia vazio aqui não é dia livre.
          </p>
        ) : rotulosOcultos.length > 0 && (
          <p className="text-xs text-slate-600">
            {MARK.WARN} Escondido: {rotulosOcultos.join(', ')}.
          </p>
        )}
        <ul className="flex flex-wrap items-center gap-x-1 gap-y-1 sm:ml-auto text-xs text-slate-600">
          {LEGENDA.map(({ chave, rotulo, cor, borda }) => {
            const ligado = !ocultos.has(chave);
            return (
              <li key={chave}>
                <button
                  type="button"
                  role="checkbox"
                  aria-checked={ligado}
                  onClick={() => alternar(chave)}
                  title={ligado ? `Esconder ${rotulo}` : `Mostrar ${rotulo}`}
                  className={`inline-flex items-center gap-1.5 px-1.5 py-1 rounded hover:bg-slate-100 transition-colors
                              ${ligado ? '' : 'text-slate-400 line-through'}`}
                >
                  <span aria-hidden="true"
                    className={`w-3 h-3 rounded-sm border-2 ${borda} ${ligado ? cor : 'bg-white'}`} />
                  {rotulo}
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      {/*
        * Na semana, a regua carrega a DATA, como em qualquer agenda: o dia fica
        * grande em cima da coluna, e hoje ganha o circulo. Com isso a celula
        * para de repetir o numero e devolve a primeira linha aos eventos.
        */}
      <div className="grid grid-cols-7 mb-1">
        {(modo === 'semana' ? dias : ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab']).map((v, i) => {
          if (modo !== 'semana') {
            return (
              <div key={v} className="text-center text-xs font-medium text-slate-500 py-1 uppercase tracking-wide">
                {v}
              </div>
            );
          }
          const hoje = v === todayStr;
          return (
            <div key={v} className="text-center py-1">
              <p className={`text-xs uppercase tracking-wide ${hoje ? 'text-blue-600 font-semibold' : 'text-slate-500'}`}>
                {['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sab'][i]}
              </p>
              <p className={`mx-auto mt-0.5 w-8 h-8 leading-8 text-lg tabular-nums rounded-full ${
                hoje ? 'bg-blue-600 text-white font-medium' : 'text-slate-700'}`}>
                {Number(v.slice(8, 10))}
              </p>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-7 gap-px bg-slate-200 rounded-lg overflow-hidden border border-slate-200">
        {dias.map((dateStr, i) => {
          if (!dateStr) return <div key={`e-${i}`} className={`bg-slate-50 ${alturaDaCelula}`} />;
          const dayEvents = events[dateStr] || [];
          const isToday = dateStr === todayStr;
          return (
            <div
              key={dateStr}
              // O espaço vazio do dia marca uma OPU nele, como clicar num
              // horário livre no Google Agenda. Os eventos param o clique.
              onClick={novoServico ? () => novoServico(dateStr) : undefined}
              title={novoServico ? 'Clique para marcar uma OPU neste dia' : undefined}
              className={`p-1 ${alturaDaCelula} ${novoServico ? 'cursor-pointer' : ''} ${
                // Na semana a data ja esta na regua, entao hoje se marca pela
                // coluna inteira — e um fundo nao rouba linha de conteudo, como
                // o anel de duas bordas roubava.
                isToday
                  ? (modo === 'semana' ? 'bg-blue-50' : 'bg-white ring-2 ring-inset ring-blue-400')
                  : 'bg-white'}`}
            >
              {modo === 'mes' && (
                <div className={`text-xs font-medium mb-1 ${isToday ? 'text-blue-600 font-bold' : 'text-slate-600'}`}>
                  {Number(dateStr.slice(8, 10))}
                </div>
              )}
              <div className="space-y-1">
                {dayEvents.map((ev, idx) => {
                  const venceu = ev.prazo && dateStr < todayStr;
                  const det = ev.prazo ? null : detalheDoEvento(ev);
                  /*
                   * CLIENTE, FAZENDA E TRABALHO em todo evento. Com dois
                   * serviços do mesmo cliente no mesmo dia, "OPU — Fulano"
                   * duas vezes não diz qual é qual: o que separa um do outro
                   * é o número do trabalho e o lote. O nome vem do cadastro,
                   * não da cópia gravada no serviço, que envelhece.
                   */
                  const doCadastro = clienteDo(ev.service);
                  const cliente = identidadeDoCliente(doCadastro).principal;
                  // A fazenda do SERVIÇO: com duas, é ela que diz aonde ir.
                  const fazenda = (localDoServico(ev.service, doCadastro)?.nome || '').trim();
                  const trabalho = [
                    ev.service.service_code && `#${ev.service.service_code}`,
                    ev.service.service_label,
                  ].filter(Boolean).join(' · ');
                  // Quem vai: o veterinário olha a agenda para saber com quem.
                  const equipe = ev.prazo ? '' : nomesDaEquipe(equipeDaEtapa(ev.service.equipe_das_etapas, ev.type));
                  return (
                    <button
                      key={idx}
                      onClick={(e) => {
                        e.stopPropagation();
                        setBalao({ evento: ev, ancora: e.currentTarget.getBoundingClientRect() });
                      }}
                      title={[EVENT_TYPES[ev.type].label, cliente, fazenda, trabalho, equipe].filter(Boolean).join(' - ')}
                      className={`w-full text-left text-xs pl-1.5 pr-1 py-0.5 rounded-r block
                                  border-l-4 transition-colors ${corDoEvento(ev, venceu)}`}
                    >
                      <span className="font-semibold block truncate">
                        {venceu && `${MARK.WARN} `}{EVENT_TYPES[ev.type].label}
                        {modo === 'mes' && <span className="font-normal"> · {cliente}</span>}
                      </span>
                      {/* Na semana cabe dizer de quem e, de que fazenda, de
                          que trabalho e quanto. No mes, duas linhas: etapa e
                          cliente, depois fazenda e trabalho — e num prazo a
                          segunda linha e o VALOR, que e o assunto dele. */}
                      {modo === 'semana' ? (
                        <>
                          <span className="block truncate">{cliente}</span>
                          {fazenda && fazenda !== cliente && (
                            <span className="block truncate">{fazenda}</span>
                          )}
                          {trabalho && (
                            <span className="block truncate opacity-75">{trabalho}</span>
                          )}
                          {equipe && (
                            <span className="block truncate italic">{equipe}</span>
                          )}
                          {/* O mesmo detalhe da imagem exportada: o resultado
                              no que já foi feito, o que vai ser trabalhado no
                              que está agendado. O prazo já diz o valor abaixo. */}
                          {det?.texto && (
                            <span className="block truncate font-medium">
                              {det.feito && `${MARK.OK} `}{det.texto}
                            </span>
                          )}
                          {ev.prazo?.valor > 0 && (
                            <span className="block tabular-nums font-semibold">
                              R$ {Number(ev.prazo.valor).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="block truncate opacity-80">
                          {ev.prazo?.valor > 0
                            ? <span className="tabular-nums font-medium">
                                R$ {Number(ev.prazo.valor).toLocaleString('pt-BR', { minimumFractionDigits: 0 })}
                              </span>
                            : [fazenda !== cliente && fazenda, trabalho].filter(Boolean).join(' · ')}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {exportando && (
        <ExportarCalendario
          eventos={events}
          servicos={servicosVisiveis}
          clientes={clients}
          pendencias={pendencias}
          cobranca={cobranca}
          laboratoryId={laboratoryId}
          podeFinanceiro={podeFinanceiro}
          rotuloDoEvento={(t) => EVENT_TYPES[t]?.label || t}
          laboratorio={laboratorio}
          hoje={todayStr}
          deInicial={periodoNaTela[0]}
          ateInicial={periodoNaTela[1]}
          onFechar={() => setExportando(false)}
        />
      )}

      {balao && (
        <BalaoDoEvento
          evento={balao.evento}
          ancora={balao.ancora}
          onFechar={() => setBalao(null)}
          onCadastro={() => { const s = balao.evento.service; setBalao(null); abrirCadastro(s); }}
          // A etapa do evento clicado: quem abre por uma TE do calendário cai
          // na aba da TE, e não na que a corrente adivinharia.
          onEtapas={() => {
            const { service, type } = balao.evento;
            setBalao(null);
            openWorkflowModal(service, ABA_DO_EVENTO[type] || null);
          }}
          onFinanceiro={podeFinanceiro
            ? () => { const s = balao.evento.service; setBalao(null); abrirFinanceiro(s); }
            : null}
          // O EVENTO diz qual ficha sai, pela mesma razão do `onEtapas` acima:
          // quem clica numa TE do calendário quer a ficha da TE, não a da
          // etapa que a corrente adivinharia. Evento sem ficha esconde o botão.
          onFicha={etapaDaFicha(balao.evento.service, fichaDoEvento(balao.evento.type))
            ? () => {
                const { service, type } = balao.evento;
                setBalao(null);
                imprimirFicha(service, fichaDoEvento(type));
              }
            : null}
        />
      )}
    </div>
  );
};

// ============================================================================
// FILTER BAR
// ============================================================================

// Filtra pelas etapas do workflow, nao por todo tipo contratavel: compra de
// embriao e diaria nao tem etapa, e filtrar por elas nao diria nada sobre o
// andamento. Derivado da corrente para que etapa nova apareca aqui sozinha.
/*
 * UMA ESCOLHA, e não três taxonomias cruzadas.
 *
 * Havia Status (3 chips), Alertas (3 chips) e Tipo (4 siglas), independentes
 * entre si: trinta e seis combinações para uma lista de duas dezenas de linhas.
 * E as três respondiam o que a própria linha já responde — o emoji diz a
 * gravidade, o texto diz o motivo, as etapas dizem o tipo. Filtro que repete a
 * coluna não recorta nada, só pede uma decisão antes de mostrar o trabalho.
 *
 * Ficaram quatro recortes exclusivos. "Precisa de ação" junta o que eram
 * Alertas e Críticos num só: a diferença entre amarelo e vermelho importa DENTRO
 * da linha, para saber o que fazer primeiro, e não para escolher o que aparece.
 *
 * O filtro de TIPO saiu sem substituto aqui. Ele era um E: marcar OPU e FIV
 * pedia serviços que têm as duas, o que é pergunta de relatório — e Relatórios
 * agrupa por modelo de cobrança e por cliente, com período. Na tela de trabalho
 * ninguém decide o que fazer hoje a partir de "só os que têm TE".
 *
 * O que recorta de verdade continua sendo o resumo de pendências logo acima:
 * ele nomeia a tarefa e conta quantos serviços a esperam.
 */
const RECORTES = [
  { id: 'tudo',      rotulo: 'Tudo' },
  { id: 'acao',      rotulo: 'Precisa de ação' },
  { id: 'andamento', rotulo: 'Em andamento' },
  { id: 'fim',       rotulo: 'Finalizados' },
];

/**
 * O QUADRO ABRE EM TUDO.
 *
 * Abriu em "Precisa de ação" por um tempo, com a ideia de que quem chega de
 * manhã quer o próprio trabalho. A equipe pediu o contrário: a lista inteira
 * primeiro, e o recorte como clique. O número de "Precisa de ação" continua no
 * botão, então o trabalho parado não some de vista.
 *
 * "Precisa de ação" é uma pergunta só, e cobre as duas naturezas: etapa que
 * passou da data sem lançamento, DG60 fora do prazo, entrada a emitir, serviço
 * pronto sem fatura e parcela vencida.
 */
const PADRAO_DO_QUADRO = 'tudo';

const FilterBar = ({
  search, onSearch, recorte, onRecorte, comAcao = 0, total, filtered, onClear,
}) => {
  // O padrão não conta como filtro aposto: o "Limpar" aparece quando alguém se
  // afastou dele, e não ao abrir a tela.
  const temFiltro = !!search || recorte !== PADRAO_DO_QUADRO;
  // Com busca, o recorte não vale (ver `filteredServices`), e o botão aceso
  // diria o contrário.
  const buscando = !!search.trim();

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mb-3">
      <div className="relative flex-1 min-w-[14rem] max-w-sm">
        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          type="text"
          // O que ela procura, escrito. Dizia "Buscar cliente..." e procurava
          // três nomes; agora procura também o número e o lote do serviço, que
          // é o que se tem na mão quando alguém liga perguntando de um.
          placeholder="Buscar serviço, cliente ou fazenda"
          value={search}
          onChange={e => onSearch(e.target.value)}
          className="w-full pl-8 pr-8 py-1.5 text-sm border border-slate-300 rounded-lg
                     focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        {search && (
          <button onClick={() => onSearch('')}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            aria-label="Limpar busca">
            <X size={13} />
          </button>
        )}
      </div>

      <div className={`inline-flex rounded-md border border-slate-300 overflow-hidden ${buscando ? 'opacity-50' : ''}`}
        title={buscando ? 'A busca procura em todos os serviços' : undefined}>
        {RECORTES.map(r => (
          <button
            key={r.id}
            onClick={() => onRecorte(r.id)}
            aria-pressed={!buscando && recorte === r.id}
            className={`px-2.5 py-1.5 text-xs font-medium transition-colors border-l first:border-l-0
                        border-slate-200 ${!buscando && recorte === r.id
                          ? 'bg-slate-800 text-white'
                          : 'bg-white text-slate-600 hover:bg-slate-50'}`}
          >
            {r.rotulo}
            {/* A contagem VIVE NO BOTÃO, e não numa faixa acima da lista.
                Era um resumo de pendências com um botão por tipo, que obrigava
                a escolher um tipo para ver o trabalho; o número que ele
                carregava é a única coisa que valia, e ele cabe aqui. */}
            {r.id === 'acao' && comAcao > 0 && (
              <span className={`ml-1.5 tabular-nums ${recorte === r.id ? 'text-slate-300' : 'text-slate-400'}`}>
                {comAcao}
              </span>
            )}
          </button>
        ))}
      </div>

      {temFiltro && (
        <button onClick={onClear}
          className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 transition-colors">
          <X size={12} /> Limpar
        </button>
      )}

      <span className="ml-auto text-xs text-slate-500">
        {filtered !== total ? <><b>{filtered}</b> de {total}</> : <b>{total}</b>} serviço{total !== 1 ? 's' : ''}
      </span>
    </div>
  );
};

// ============================================================================
// MAIN DASHBOARD TAB
// ============================================================================

const VIEW_MODES = [
  { id: 'pipeline', label: 'Pipeline',   Icon: LayoutList   },
  { id: 'kanban',   label: 'Kanban',     Icon: Columns      },
  { id: 'calendar', label: 'Calendário', Icon: CalendarIcon },
];

/*
 * O RESUMO DO PERÍODO SAIU DAQUI.
 *
 * Eram três blocos empilhados acima do quadro de serviços: o seletor de período,
 * quatro cartões de dinheiro e quatro de performance técnica. Nenhum deles muda
 * o que se faz no dia, e os três empurravam para baixo da dobra justamente a
 * lista e o calendário — que é o trabalho.
 *
 * Os cartões de dinheiro tinham um problema pior que o espaço: liam
 * `billed_value`, que o gatilho preenche só com faturas de serviço, e escreviam
 * "Faturado" ao lado de um "Faturado" da aba Financeiro que somava serviço e
 * entrada. Dois números diferentes, o mesmo rótulo, a mesma sessão. Agora há um
 * lugar só, o relatório, e ele diz qual responde o quê.
 *
 * A performance técnica foi para os painéis de Embriões e Prenhez, em
 * Relatórios, que já respondem a mesma pergunta com quebra por doadora e touro
 * — coisa que quatro barras de porcentagem não faziam.
 */
const DashboardTab = ({
  clients = [],
  calculateServiceValue,
  services,
  getServiceStatus,
  openWorkflowModal,
  // As outras duas portas do serviço. O financeiro é restrito: `podeFinanceiro`
  // vem de fora porque a regra de papel mora no FIVSystem, não aqui.
  abrirCadastro,
  abrirFinanceiro,
  podeFinanceiro = false,
  // Criar serviço. Vinha da aba de Serviços, que sai.
  novoServico = null,
  // Excluir. `null` esconde a lixeira, que é como um papel sem permissão a vê.
  excluirServico = null,
  // O serviço que acabou de ser criado, para o quadro abrir nele.
  servicoEmDestaque = null,
  currentLab,
  parcelasPorServico = {},
  entradaPagaPorServico = {},
  entradaEmitidaPorServico = {},
}) => {
  /**
   * Imprime a ficha de campo da etapa.
   *
   * Mora aqui, e não em cada view, porque as três precisam da mesma coisa e
   * só o pai tem o laboratório — a ficha da TE busca os embriões, e a consulta
   * é filtrada por ele.
   */
  const imprimirFicha = useCallback(
    (service, etapa = null) =>
      gerarFichaDeCampo(service, etapa, { laboratoryId: currentLab?.id }),
    [currentLab],
  );

  /**
   * Por que cada serviço é crítico, calculado uma vez por lista.
   *
   * Memoizado por serviço porque a linha do pipeline chama isto durante o
   * render, e a conta varre as parcelas — refazê-la a cada célula seria a
   * mesma armadilha do `services.filter` dentro do map.
   */
  const criticidade = useMemo(() => {
    const cache = {};
    (services || []).forEach(s => {
      // Os argumentos são montados em `motivosDoServico`, e não aqui: era a
      // montagem em dois lugares que fazia o Financeiro do serviço contar os
      // mesmos fatos com outro resultado.
      cache[s.id] = motivosDoServico(s, {
        parcelas: parcelasPorServico[s.id] || [],
        valor: calculateServiceValue(s),
        entradaPaga: entradaPagaPorServico[s.id] || 0,
        entradaEmitida: !!entradaEmitidaPorServico[s.id],
        laboratory: currentLab,
      });
    });
    return (s) => cache[s?.id] || { critico: false, motivos: [] };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [services, parcelasPorServico, entradaPagaPorServico, entradaEmitidaPorServico, currentLab]);

  /**
   * O que cobrar e faturar, uma linha por serviço, para o resumo de gestão.
   *
   * A MESMA situação que a tela do Financeiro desenha — entrada, serviço e
   * próximo passo —, e não uma lista por motivo, que repetia o mesmo serviço
   * uma vez por motivo. Vazia para quem não vê o financeiro.
   */
  const cobranca = useMemo(() => (podeFinanceiro
    ? linhasDeCobranca(services || [], s => situacaoNoContexto(s, {
      parcelasPorServico, entradaEmitidaPorServico, laboratory: currentLab, services,
      valor: calculateServiceValue(s),
      motivos: criticidade(s).motivos,
    }))
    : []),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [podeFinanceiro, services, parcelasPorServico, entradaEmitidaPorServico, currentLab, criticidade]);

  /**
   * O que está esperando alguém, no laboratório inteiro.
   *
   * Sobre `services` e não sobre `filteredServices`: o painel responde "o que
   * preciso fazer", e essa pergunta não muda porque alguém digitou um nome na
   * busca. Pendência escondida por filtro é pendência esquecida.
   */
  const pendencias = useMemo(
    () => pendenciasDoLaboratorio(services, {
      laboratory: currentLab,
      entradaEmitida: entradaEmitidaPorServico,
      // O dinheiro entra no cálculo por aqui: faturar e cobrar são trabalho
      // como lançar etapa, e ficavam fora da conta do que está parado. Só para
      // quem pode abrir a porta do financeiro — a contagem do que há por lá não
      // serve a quem não pode agir sobre ela.
      criticidade: podeFinanceiro ? criticidade : null,
    }),
    // `criticidade` e' um useMemo deste mesmo componente, e muda quando as
    // parcelas mudam — entao ela entra. `podeFinanceiro` e' um booleano de
    // papel, constante na sessao, e entra por completude.
    [services, currentLab, entradaEmitidaPorServico, criticidade, podeFinanceiro],
  );

  // View
  const [viewMode, setViewMode] = useState('pipeline');

  // Sort (Pipeline)
  const [sortField, setSortField] = useState('date');
  const [sortDir,   setSortDir]   = useState('desc');

  // Filters
  const [search,  setSearch]  = useState('');
  const [recorte, setRecorte] = useState(PADRAO_DO_QUADRO);

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const clearFilters = () => {
    setSearch('');
    setRecorte(PADRAO_DO_QUADRO);
  };

  const filteredServices = useMemo(() => {
    let list = services;

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      /*
       * Procura o SERVIÇO e o CLIENTE, que é o que se tem na mão.
       *
       * Eram três nomes de cliente: quem digita "Nezinho" quer o serviço da
       * fazenda dele, e quem digita "Conceição" quer o mesmo serviço. Faltavam
       * as duas coisas que identificam o serviço em si — o número e o lote —, e
       * eram justamente elas que apareciam no telefone e no WhatsApp.
       */
      /*
       * A BUSCA PROCURA EM TODOS, e o recorte não se soma a ela.
       *
       * Quem digita um nome quer AQUELE serviço, esteja ele finalizado ou não.
       * Com os dois somados, a busca num recorte errado respondia "nenhum
       * serviço encontrado" para um serviço que existe — e a pessoa
       * concluía que ele não existia.
       */
      return list.filter(s => {
        const { principal, secundario } = identidade(s, clients);
        return [s.client_name, principal, secundario, s.service_code, s.service_label]
          .some(v => (v || '').toLowerCase().includes(q));
      });
    }

    if (recorte === 'acao') {
      /*
       * O conjunto vem de `pendenciasDoLaboratorio`, e não de
       * `getCompositeStatus`.
       *
       * O status composto era uma TERCEIRA definição de "algo está errado",
       * paralela à criticidade e às pendências, e mais grosseira: o vermelho
       * dele era "faturado há mais de 15 dias e não pago", que não é o prazo de
       * ninguém — o vencimento da parcela é. E ele não sabia de entrada a
       * emitir nem de serviço pronto sem fatura.
       *
       * O emoji da linha continua vindo dele, porque ali a pergunta é outra: a
       * GRAVIDADE, para ordenar a coluna de status.
       */
      list = filtraPorPendencia(list, pendencias, 'acao');
    } else if (recorte === 'andamento') {
      list = list.filter(s => getServiceStatus(s) !== 'Finalizado');
    } else if (recorte === 'fim') {
      list = list.filter(s => getServiceStatus(s) === 'Finalizado');
    }

    return list;
  }, [services, clients, search, recorte, pendencias, getServiceStatus]);

  return (
    <div className="space-y-6">
      {/* ── Serviços ── */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200">
        {/* Header: title + view switcher */}
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 pt-4 pb-3 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <h3 className="text-base font-semibold text-slate-900">Serviços</h3>
            {/* NOVO SERVIÇO mora aqui agora.
                Ele só existia na aba de Serviços, que vai sair — e sem ele o
                Dashboard não fecharia o ciclo: dava para abrir, lançar e
                faturar um serviço, mas não para criar um. */}
            {novoServico && (
              <button
                onClick={() => novoServico()}
                className="inline-flex items-center gap-1.5 bg-blue-600 text-white px-3 py-1.5
                           rounded-lg text-xs font-medium hover:bg-blue-700 transition-colors"
              >
                <Plus size={14} />
                <span>Novo serviço</span>
              </button>
            )}
          </div>
          <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
            {VIEW_MODES.map(({ id, label, Icon }) => (
              <button
                key={id}
                onClick={() => setViewMode(id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  viewMode === id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-800'
                }`}
              >
                <Icon size={13} />
                <span className="hidden sm:inline">{label}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="p-4">
          {/* O CALENDÁRIO NÃO FILTRA. Ele responde "o que acontece neste
              dia", e um evento que some porque a lista estava em
              "Finalizados" é um dia que parece livre sem estar. Sem filtro
              aplicado, a barra não aparece — ela prometeria um recorte que
              não acontece. */}
          {viewMode !== 'calendar' && (
            <FilterBar
              search={search}             onSearch={setSearch}
              recorte={recorte}
              // Escolher um recorte com a busca preenchida é pedir o recorte:
              // a busca sai, senão o clique não mudaria nada na lista.
              onRecorte={(r) => { setRecorte(r); setSearch(''); }}
              comAcao={pendencias.servicosComAcao}
              total={services.length}     filtered={filteredServices.length}
              onClear={clearFilters}
            />
          )}

          {/* Active view */}
          {viewMode === 'pipeline' && (
            <PipelineView
              abrirCadastro={abrirCadastro}
              abrirFinanceiro={abrirFinanceiro}
              podeFinanceiro={podeFinanceiro}
              criticidade={criticidade}
              clients={clients}
              services={filteredServices}
              getServiceStatus={getServiceStatus}
              openWorkflowModal={openWorkflowModal}
              imprimirFicha={imprimirFicha}
              servicoEmDestaque={servicoEmDestaque}
              excluirServico={excluirServico}
              sortField={sortField}
              sortDir={sortDir}
              onSort={handleSort}
            />
          )}
          {viewMode === 'kanban' && (
            <KanbanView clients={clients} services={filteredServices}
              openWorkflowModal={openWorkflowModal}
              abrirCadastro={abrirCadastro} abrirFinanceiro={abrirFinanceiro}
              imprimirFicha={imprimirFicha}
              podeFinanceiro={podeFinanceiro} />
          )}
          {viewMode === 'calendar' && (
            <CalendarView parcelasPorServico={parcelasPorServico}
              laboratorio={currentLab?.name}
              laboratoryId={currentLab?.id}
              clients={clients}
              pendencias={pendencias}
              cobranca={cobranca}
              entradaPagaPorServico={entradaPagaPorServico}
              services={services} openWorkflowModal={openWorkflowModal}
              abrirCadastro={abrirCadastro} abrirFinanceiro={abrirFinanceiro}
              calculateServiceValue={calculateServiceValue}
              imprimirFicha={imprimirFicha}
              novoServico={novoServico ? (dia) => novoServico({ date: dia, opu_no_dia: dia }) : null}
              podeFinanceiro={podeFinanceiro} />
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardTab;
